<template>
  <main class="container">
    <div class="page-wrapper">
      <h1 class="page-title" data-testid="page-title">
        Invite Your Housemates
      </h1>

      <p class="invite-text">
        Now that you’ve joined Split The Bills, it’s time to invite your
        housemates to join.
      </p>

      <div
        class="share-info"
        :class="{ 'big-spacing': canUseNativeShare }"
        v-if="!canUseNativeShare"
      >
        <h2>Share account link now</h2>
        <span>
          If you don’t have your housemates' details to hand, you can invite
          them by sharing the link below.
        </span>
      </div>

      <SocialShare
        class="congrats-share"
        :share-link="shareLink"
        native-share-title="Share Invite Link Now"
        :share-subject="shareSubject"
        :share-body="shareBody"
        :whats-app-share-body="whatsAppShareBody"
        :facebook-title="facebookTitle"
        :facebook-description="facebookDescription"
        :facebook-image-url="facebookImageUrl"
        :email-via-modal="true"
        :inline="true"
        @show-modal="setShareModalVisible"
        @whatsAppShare="trackWhatsAppShare"
        @facebookShare="trackFacebookShare"
        @emailShare="trackEmailShare"
        @copyShareLink="trackCopyLink"
        @nativeShare="trackNativeShare"
      />

      <div class="share-info">
        <h2>Invite Your Housemates by Email</h2>
        <span>
          You can also invite your housemates by email. We’ll send them a unique
          link so they can join your account.
        </span>
      </div>

      <button
        class="btn btn--big invite-hm8s-btn"
        data-testid="invite-hm8s-btn"
        @click="setShareModalVisible"
      >
        Invite Housemates
      </button>

      <hr />

      <h2>What happens next?</h2>
      <check-list :items="whatsNextItems" />

      <SignupShareOverlay
        :guid="guid"
        :remaining-housemate-count="remainingHousemateCount"
        @close-modal="setShareModalInvisible"
        v-if="showShareModal"
        @inviteBtnSubmit="trackInviteSubmitBtn"
      />
    </div>

    <p class="cancel-disclaimer">
      You will receive an email within 3 business days confirming your Direct
      Debit mandate has been set up.
    </p>
  </main>
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import SocialShare from "@/components/SocialShare";
import SignupShareOverlay from "@/components/SignupShareOverlay";
import CheckList from "@/components/CheckList";
import { isNativeShareSupported } from "@/utility/nativeShare";

export default {
  name: "Congratulations",
  data() {
    return {
      guid: "",
      joinLink: "",
      shareSubject: "Join our Split The Bills utilities account",
      shareBody:
        "Hi,\n\nI’ve created our utilities account with Split The Bills. You need to follow the link below to join our account so that they can get everything set up for us. It only takes 2 minutes and then we can forget all about bills!\n\n",
      showShareModal: false,
      whatsAppShareBody:
        "Join the Split The Bills account I’ve created for us. Just click this link to add your details:\n\n",
      facebookTitle:
        "Check out the bills package I’ve made for our shared bills",
      facebookDescription:
        "Let’s stay pals and not faff about or fuss over our shared bills. Split The Bills will set up and pay them for us. We just make one monthly payment each from our own bank accounts. Easy!",
      remainingHousemateCount: 0,
      whatsNextItems: [
        "You invite your housemates to join the account",
        "We’ll set up your utilities as soon as everyone has joined",
        "You won’t pay a thing until your start date",
      ],
    };
  },
  computed: {
    isLeadTenant() {
      return this.$store.getters.isLeadTenant;
    },
    facebookImageUrl() {
      return `${document.location.origin}${
        process.env.BASE_URL
      }share-generic.png`;
    },
    shareLink() {
      if (this.joinLink) {
        return this.joinLink;
      }

      return `${document.location.origin}/joining-tenant?guid=${this.guid}`;
    },
    canUseNativeShare() {
      return isNativeShareSupported();
    },
  },
  methods: {
    ...mapActions(["clearSession"]),
    ...mapMutations({
      hideBackBtn: "HIDE_BACK_BTN",
    }),
    setShareModalVisible() {
      this.trackGoogleEvent(
        "Click Invite Housemates",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Invite Housemates",
      );
      this.trackFacebookEvent("Click Invite Housemates");

      this.showShareModal = true;
    },
    setShareModalInvisible() {
      this.showShareModal = false;
    },
    trackWhatsAppShare() {
      this.trackGoogleEvent(
        "Click share via WhatsApp",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Share price WhatsApp",
      );
      this.trackFacebookEvent("Click share via WhatsApp");
    },
    trackFacebookShare() {
      this.trackGoogleEvent(
        "Click share via Facebook",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Share price Facebook",
      );
      this.trackFacebookEvent("Click share via Facebook");
    },
    trackEmailShare() {
      this.trackGoogleEvent(
        "Click share via email",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Share price email",
      );
      this.trackFacebookEvent("Click share via email");
    },
    trackInviteSubmitBtn() {
      this.trackGoogleEvent(
        "Send Housemate Invitation",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Housemate Invited",
      );
      this.trackFacebookEvent("Send Housemate Invitation");
    },
    trackCopyLink() {
      this.trackGoogleEvent(
        "Click Copy",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Copy share price link",
      );
      this.trackFacebookEvent("Click Copy");
    },
    trackNativeShare() {
      this.trackGoogleEvent(
        "Share Invite Link",
        this.isLeadTenant ? "Create Account" : "Joining Housemate",
        "Native Link Share",
      );
      this.trackFacebookEvent("Native Invite Link Share");
    },
  },
  created() {
    this.trackGoogleEvent(
      "Land on Congratulations Page",
      this.isLeadTenant ? "Create Account" : "Joining Housemate",
      this.isLeadTenant
        ? "Lead tenant completion"
        : "Confirm joining housemate completion",
    );
    if (this.isLeadTenant) {
      this.trackFacebookEvent("Land on Congratulations Page");
    } else {
      this.trackFacebookEvent("JT Lands on Congratulations Page");
    }

    // grab the info from the store before we clear it
    const accountInformation = this.$store.state.accountInformation;
    this.guid = accountInformation.guid;
    this.joinLink = accountInformation.joinLink;
    this.remainingHousemateCount = accountInformation.remainingHousemates;

    // We've finished the signup so reset the store data
    this.clearSession();
    // We don't need back button on this page.
    this.hideBackBtn();
  },
  components: {
    CheckList,
    SocialShare,
    SignupShareOverlay,
  },
};
</script>

<style lang="scss" scoped>
.container {
  text-align: center;
  max-width: 65rem;
}

.invite-text {
  font-size: $subtitle-font-size;
}

.invite-hm8s-btn {
  margin: 4rem auto;
  background-color: $brand-orange;

  &:hover {
    background-color: darken($brand-orange, 5%);
  }
}

h2 {
  color: $brand-orange;
  font-size: $button-font-size;
  font-weight: $font-bold;
}

.share-info {
  margin: 0;

  span {
    display: block;
    font-size: $subtitle-font-size;
  }
}

.cancel-disclaimer {
  font-size: 14px;
  margin: 2rem 6rem;

  @include media("<medium") {
    margin: 2rem 0;
  }
}
</style>

<style lang="scss">
.congrats-share {
  &.native-share {
    margin: 4rem auto;
  }

  .native-share-btn {
    background-color: $brand-green !important;

    &:hover {
      background-color: darken($brand-green, 5%) !important;
    }
  }
}
</style>
